'use client';

import {
  useState,
  useEffect,
  useCallback,
  useContext,
  type FC,
  useRef,
} from 'react';
import { Document, Page } from 'react-pdf';
import {
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Input,
  PDFZoom,
  Text,
} from '@/_components';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { AnnotationLayer } from './_components/AnnotationLayer';
import { UserContext } from '@/_contexts/UserProvider';
import { saveAnnotations } from '@/_api/admin-annotations';
import { ToastContext } from '@/_contexts/ToastProvider';
import type { PDFViewerProps, SelectionBox } from './PDFViewer.types';
import type { Annotation } from '@/_types/documents';

export const BASE_FONT_SIZE = 12;

export const PDFViewer: FC<PDFViewerProps> = ({ url, onPassword }) => {
  const {
    color,
    dimensions,
    mode,
    pageRotations,
    currentPage,
    previewAnnotations = [],
    previewCompanyIdForAnnotations,
    previewExtractedDocumentId,
    selectedIds,
    setCurrentPage,
    setDimensions,
    setPreviewAnnotations,
    setSelectedIds,
  } = useContext(PreviewContext);
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [showNewTextDialog, setShowNewTextDialog] = useState<boolean>(false);
  const [newText, setNewText] = useState<string>('');
  const [pendingAnnotation, setPendingAnnotation] = useState<{
    clickX: number;
    clickY: number;
    pageNumber: number;
    rect: DOMRect;
  } | null>(null);

  const pdfContainer = useRef<HTMLDivElement | null>(null);
  const containerWidth = useRef<number>(0);
  const [numPages, setNumPages] = useState<number>(0);
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  const [isSelecting, setIsSelecting] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!pdfContainer.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;

        if (containerWidth.current && newWidth !== containerWidth.current) {
          if (newWidth < containerWidth.current) {
            setDimensions((prev) => ({ ...prev, zoom: 1 }));
          } else {
            const scaleFactor = newWidth / containerWidth.current;
            setDimensions((prev) => ({
              ...prev,
              zoom: prev.zoom * scaleFactor,
            }));
          }
        }

        containerWidth.current = newWidth;
      }
    });

    resizeObserver.observe(pdfContainer.current);
    return () => resizeObserver.disconnect();
  }, [setDimensions]);

  useEffect(() => {
    if (!pdfContainer.current || numPages === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const newVisiblePages = new Set<number>();

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const pageElement = entry.target as HTMLElement;
            const pageNumber = parseInt(
              pageElement.getAttribute('data-page-number') || '0',
              10,
            );
            if (pageNumber > 0) {
              newVisiblePages.add(pageNumber);
            }
          }
        });

        // Set current page to the first visible page
        if (newVisiblePages.size > 0) {
          const firstVisiblePage = Math.min(...Array.from(newVisiblePages));
          setCurrentPage(firstVisiblePage);
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5, // Page needs to be at least 50% visible
      },
    );

    // Observe all page elements
    const pageElements =
      pdfContainer.current.querySelectorAll('[data-page-number]');
    pageElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [numPages, setCurrentPage]);

  const handlePasswordRequest = useCallback(async (): Promise<string> => {
    try {
      return await onPassword();
    } catch (error) {
      return '';
    }
  }, [onPassword]);

  const handleUpdateAnnotation = async (updated: Annotation) => {
    if (typeof previewCompanyIdForAnnotations !== 'number') return;
    if (typeof previewExtractedDocumentId !== 'number') return;

    const newAnnotations = previewAnnotations.map((a) =>
      a.id === updated.id ? updated : a,
    );

    setPreviewAnnotations(newAnnotations);

    try {
      const { error } = await saveAnnotations({
        extractedDocumentId: previewExtractedDocumentId!,
        token,
        companyId: previewCompanyIdForAnnotations!,
        annotations: newAnnotations,
      });

      if (!error) addToast('success', 'Annotation updated successfully');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const annotationElement = (e.target as Element).closest('[data-id]');

    if (annotationElement) {
      const annotationId = Number(annotationElement.getAttribute('data-id'));
      return handleAnnotationClick(e, annotationId);
    }

    if (mode !== 'view' && mode !== 'move') return;

    const rect = e.currentTarget.getBoundingClientRect();
    const { zoom } = dimensions;

    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    setSelectionBox({
      startX: x,
      startY: y,
      endX: x,
      endY: y,
    });
    setIsSelecting(true);

    if (!e.ctrlKey && !e.metaKey) {
      setSelectedIds(new Set());
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const { zoom } = dimensions;

    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    setSelectionBox((prev) =>
      prev
        ? {
            ...prev,
            endX: x,
            endY: y,
          }
        : null,
    );
  };

  const updateSelectionBoxAnnotations = useCallback(() => {
    if (!selectionBox) return;

    const { scale, zoom, height } = dimensions;

    const selectionLeft = Math.min(selectionBox.startX, selectionBox.endX);
    const selectionRight = Math.max(selectionBox.startX, selectionBox.endX);
    const selectionTop = Math.min(selectionBox.startY, selectionBox.endY);
    const selectionBottom = Math.max(selectionBox.startY, selectionBox.endY);

    const selectedAnnotations = previewAnnotations.filter((annotation) => {
      const screenX = annotation.position_data.left * scale;
      const screenY =
        (height - Math.abs(annotation.position_data.bottom * scale)) / zoom;
      const screenWidth = annotation.position_data.width * scale;
      const screenHeight = annotation.position_data.height * scale;

      return !(
        screenX + screenWidth < selectionLeft ||
        screenX > selectionRight ||
        screenY - screenHeight > selectionBottom ||
        screenY < selectionTop
      );
    });

    setSelectedIds(new Set(selectedAnnotations.map((a) => a.id)));
  }, [dimensions, previewAnnotations, selectionBox, setSelectedIds]);

  const handleMouseUp = () => {
    if (selectionBox) {
      updateSelectionBoxAnnotations();
    }
    setIsSelecting(false);
    setSelectionBox(null);
  };

  const measureTextWidth = useCallback(
    (text: string): number => {
      const measurer = document.createElement('div');
      measurer.style.cssText = `
        position: absolute;
        visibility: hidden;
        font-size: ${BASE_FONT_SIZE * dimensions.scale}px;
        padding: 6px 12px;
        white-space: nowrap;
        font-family: Arial, sans-serif;
        font-weight: 400;
        line-height: 1.2;
        transform-origin: center;
        transform: scale(${dimensions.scale});
      `;

      measurer.innerText = text;
      document.body.appendChild(measurer);

      const rect = measurer.getBoundingClientRect();
      document.body.removeChild(measurer);

      const widthInPdfUnits = rect.width / dimensions.scale;
      const extraPadding = 12;
      const finalWidth = widthInPdfUnits + extraPadding;

      const minWidth = 80;
      const maxWidth = 400;

      return Math.min(maxWidth, Math.max(minWidth, finalWidth));
    },
    [dimensions.scale],
  );

  const createAnnotation = async ({
    clickX,
    clickY,
    pageNumber,
    rect,
    text = null,
  }: {
    clickX: number;
    clickY: number;
    pageNumber: number;
    rect: DOMRect;
    text?: string | null;
  }): Promise<void> => {
    const { scale, zoom, height } = dimensions;

    const checkSize = 25;
    const textHeight = BASE_FONT_SIZE + 16;

    const annotWidth = text ? measureTextWidth(text) : checkSize / scale;
    const annotHeight = text ? textHeight / scale : checkSize / scale;

    const newAnnotation: Partial<Annotation> = {
      position_data: {
        left: Math.round(clickX / scale - annotWidth / 2),
        width: Math.round(annotWidth),
        bottom: Math.round(
          -(height / scale - clickY / scale - annotHeight / 2),
        ),
        height: Math.round(annotHeight),
        pageWidth: Math.round(rect.width / zoom / scale),
        pageHeight: Math.round(height / scale),
      },
      style: `color: ${color}`,
      text,
      shape_type: mode,
      page: pageNumber,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const updatedAnnotations = await saveAnnotations({
      extractedDocumentId: previewExtractedDocumentId!,
      token,
      companyId: previewCompanyIdForAnnotations!,
      annotations: [...previewAnnotations, newAnnotation],
    });

    setPreviewAnnotations(updatedAnnotations);
  };

  const handleAnnotationClick = (e: React.MouseEvent, annotationId: number) => {
    e.stopPropagation();

    if (mode === 'move') return;

    setSelectedIds((prev) => {
      const newSelection = new Set(prev);

      if (e.ctrlKey || e.metaKey) {
        prev.has(annotationId)
          ? newSelection.delete(annotationId)
          : newSelection.add(annotationId);
      } else {
        newSelection.clear();
        newSelection.add(annotationId);
      }

      return newSelection;
    });
  };

  const handleClick = (
    event: React.MouseEvent<HTMLDivElement>,
    pageNumber: number,
  ): void => {
    if (mode === 'view' || mode === 'move' || isSelecting) return;

    const annotationElement = (event.target as Element).closest('[data-id]');
    if (annotationElement) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const position = {
      clickX: (event.clientX - rect.left) / dimensions.zoom,
      clickY: (event.clientY - rect.top) / dimensions.zoom,
      pageNumber,
      rect,
    };

    if (mode === 'textbox') {
      setPendingAnnotation(position);
      setShowNewTextDialog(true);
      return;
    }

    createAnnotation(position);
  };

  const handleCreateNewText = async (): Promise<void> => {
    if (!pendingAnnotation || !newText.trim()) return;

    await createAnnotation({
      ...pendingAnnotation,
      text: newText.trim(),
    });

    setShowNewTextDialog(false);
    setNewText('');
    setPendingAnnotation(null);
  };

  const handleDialogClose = (): void => {
    setShowNewTextDialog(false);
    setNewText('');
    setPendingAnnotation(null);
  };

  const handlePageRenderSuccess = (page: any): void => {
    const viewport = page.getViewport({ scale: 1 });
    const container = pdfContainer.current;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const scale = containerWidth / viewport.width;

    setDimensions((prev) => ({
      width: viewport.width * scale,
      height: viewport.height * scale,
      scale,
      zoom: prev.zoom,
    }));
  };

  const handleLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
    setIsLoading(false);
    setLoadError(null);
  };

  const handleLoadError = (error: Error): void => {
    setLoadError('Failed to load PDF document. Please try again.');
    setIsLoading(false);

    addToast('error', null, { error });
  };

  return (
    <>
      <Dialog
        isOpen={showNewTextDialog}
        onClose={handleDialogClose}
        isNarrow={true}
        title="New annotation"
      >
        <Dialog.Content>
          <Input
            label="Annotation text"
            value={newText}
            maxLength={32}
            onChange={(e) => setNewText(e.target.value)}
            placeholder="Enter annotation text"
            autoFocus={true}
          />
        </Dialog.Content>
        <Dialog.Footer>
          <ButtonGroup align="right">
            <Button
              intent="secondary"
              onClick={handleDialogClose}
              value="Cancel"
            />
            <Button
              intent="primary"
              onClick={handleCreateNewText}
              value="Create"
              isDisabled={!newText.trim()}
            />
          </ButtonGroup>
        </Dialog.Footer>
      </Dialog>

      <Dialog.Content>
        {loadError ? (
          <Flex padding={6} isCentered={true}>
            <Text type="small" color="secondary">
              {loadError}
            </Text>
          </Flex>
        ) : (
          <>
            {isLoading && (
              <Flex padding={6} isCentered={true}>
                <Text type="small" color="secondary">
                  Loading PDF...
                </Text>
              </Flex>
            )}
            <PDFZoom
              zoom={dimensions.zoom}
              onZoomIn={() =>
                setDimensions((prev) => ({
                  ...prev,
                  zoom: Math.min(3, prev.zoom + 0.1),
                }))
              }
              onZoomOut={() =>
                setDimensions((prev) => ({
                  ...prev,
                  zoom: Math.max(0.5, prev.zoom - 0.1),
                }))
              }
              onResetZoom={() =>
                setDimensions((prev) => ({ ...prev, zoom: 1 }))
              }
              isFixed
            />
            <div
              ref={pdfContainer}
              style={{
                transform: `scale(${dimensions.zoom})`,
                transformOrigin: 'top left',
              }}
            >
              <Document
                file={url}
                loading={<></>}
                onLoadSuccess={handleLoadSuccess}
                onLoadError={handleLoadError}
                onPassword={handlePasswordRequest}
              >
                {Array.from({ length: numPages }, (_, index) => {
                  const pageNumber = index + 1;
                  const pageRotation = pageRotations[pageNumber] || 0;

                  return (
                    <div
                      key={`page_${pageNumber}`}
                      data-page-number={pageNumber}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onClick={(e) => {
                        if (!(e.target as Element).closest('[data-id]')) {
                          handleClick(e, pageNumber);
                        }
                      }}
                      style={{
                        position: 'relative',
                        cursor: mode === 'view' ? 'default' : 'crosshair',
                        marginBottom: '20px',
                        justifySelf: 'center',
                      }}
                    >
                      <div
                        style={{
                          position: 'relative',
                          transform: `rotate(${pageRotation}deg)`,
                          transformOrigin: 'center center',
                          border:
                            pageNumber === currentPage
                              ? '2px solid var(--border-primary)'
                              : 'none',
                          borderRadius:
                            pageNumber === currentPage ? '8px' : '0',
                          padding: pageNumber === currentPage ? '8px' : '0',
                        }}
                      >
                        <Page
                          pageNumber={pageNumber}
                          onRenderSuccess={handlePageRenderSuccess}
                        />
                        <AnnotationLayer
                          annotations={previewAnnotations.filter(
                            (a) => a.page === pageNumber,
                          )}
                          selectedIds={selectedIds}
                          selectionBox={selectionBox}
                          onSelect={(ids: Array<any>) =>
                            setSelectedIds(new Set(ids))
                          }
                          onUpdateAnnotation={handleUpdateAnnotation}
                        />
                      </div>
                    </div>
                  );
                })}
              </Document>
            </div>
          </>
        )}
      </Dialog.Content>
    </>
  );
};
